import { ChangeEvent, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  columnLabel,
  extractTenderSourceLines,
  headersForRow,
  parseTenderWorkbook,
  sha256File,
  suggestMappingForHeaderRow,
  type ParsedTenderWorkbook,
  type TenderColumnMapping,
} from "../lib/tenderLpuParser";
import type { AppProfile } from "../types";

type Props = {
  profile: AppProfile;
  onImported: (estimateId: string) => void | Promise<void>;
  onCancel: () => void;
};

function safeFileName(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, "_");
}

function asColumn(value: string): number | null {
  return value === "" ? null : Number(value);
}

export default function TenderLpuImporter({
  profile,
  onImported,
  onCancel,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [workbook, setWorkbook] = useState<ParsedTenderWorkbook | null>(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState<TenderColumnMapping>({
    code: null,
    description: null,
    unit: null,
    quantity: null,
  });

  const [reference, setReference] = useState("");
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [coefficient, setCoefficient] = useState("1.30");

  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const selectedSheet = workbook?.sheets[sheetIndex] ?? null;
  const headers = useMemo(
    () => (selectedSheet ? headersForRow(selectedSheet, headerRow) : []),
    [selectedSheet, headerRow],
  );

  const sourceLines = useMemo(
    () =>
      selectedSheet
        ? extractTenderSourceLines(selectedSheet, headerRow, mapping)
        : [],
    [selectedSheet, headerRow, mapping],
  );

  const preview = sourceLines.slice(0, 8);

  async function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const nextFile = event.target.files?.[0] ?? null;
    setFile(nextFile);
    setWorkbook(null);
    setError("");
    setNotice("");

    if (!nextFile) return;

    const extension = nextFile.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xlsm"].includes(extension ?? "")) {
      setError("Nesta primeira versão, importe ficheiros .xlsx ou .xlsm.");
      return;
    }

    setParsing(true);

    try {
      const parsed = await parseTenderWorkbook(nextFile);
      if (parsed.sheets.length === 0) {
        throw new Error("O Excel não contém folhas legíveis.");
      }

      const nextSheetIndex = parsed.suggestedSheetIndex;
      const sheet = parsed.sheets[nextSheetIndex];

      setWorkbook(parsed);
      setSheetIndex(nextSheetIndex);
      setHeaderRow(sheet.detectedHeaderRow);
      setMapping(sheet.detectedMapping);

      if (!name.trim()) {
        setName(nextFile.name.replace(/\.(xlsx|xlsm)$/i, ""));
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Não foi possível ler o ficheiro Excel.",
      );
    } finally {
      setParsing(false);
    }
  }

  function changeSheet(nextIndex: number) {
    if (!workbook) return;

    const sheet = workbook.sheets[nextIndex];
    setSheetIndex(nextIndex);
    setHeaderRow(sheet.detectedHeaderRow);
    setMapping(sheet.detectedMapping);
    setError("");
  }

  function changeHeaderRow(value: number) {
    if (!selectedSheet) return;

    const safeRow = Math.max(0, Math.min(value, selectedSheet.rows.length - 1));
    setHeaderRow(safeRow);
    setMapping(suggestMappingForHeaderRow(selectedSheet, safeRow));
  }

  async function importWorkbook() {
    if (!file || !workbook || !selectedSheet) return;

    setError("");
    setNotice("");

    const parsedCoefficient = Number(coefficient.replace(",", "."));
    if (!Number.isFinite(parsedCoefficient) || parsedCoefficient <= 0) {
      setError("O coeficiente geral tem de ser superior a zero.");
      return;
    }

    if (!name.trim()) {
      setError("Indique o nome da proposta/obra.");
      return;
    }

    if (mapping.description === null) {
      setError("É necessário indicar a coluna da descrição.");
      return;
    }

    if (sourceLines.length === 0) {
      setError(
        "Não encontrei linhas para importar. Confirme a folha, a linha de cabeçalho e o mapeamento.",
      );
      return;
    }

    setImporting(true);

    let storagePath = "";

    try {
      const hash = await sha256File(file);
      const fileName = safeFileName(file.name);
      storagePath = `${profile.user_id}/${hash}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("estimate-source-files")
        .upload(storagePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType:
            file.type ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

      if (uploadError) {
        if (
          uploadError.message.toLowerCase().includes("already exists") ||
          uploadError.message.toLowerCase().includes("duplicate")
        ) {
          throw new Error("Este ficheiro já foi carregado anteriormente.");
        }

        throw uploadError;
      }

      const { data, error: importError } = await supabase.rpc(
        "import_tender_lpu_to_estimate",
        {
          estimate_data: {
            reference: reference.trim() || null,
            name: name.trim(),
            client_name: client.trim() || null,
            default_coefficient: parsedCoefficient,
          },
          source_file: {
            file_name: file.name,
            file_sha256: hash,
            storage_path: storagePath,
          },
          sheet_data: {
            sheet_name: selectedSheet.name,
            sheet_count: workbook.sheets.length,
            header_row: headerRow + 1,
            mapping: {
              code:
                mapping.code === null ? null : columnLabel(mapping.code),
              description:
                mapping.description === null
                  ? null
                  : columnLabel(mapping.description),
              unit:
                mapping.unit === null ? null : columnLabel(mapping.unit),
              quantity:
                mapping.quantity === null
                  ? null
                  : columnLabel(mapping.quantity),
            },
          },
          source_lines: sourceLines,
        },
      );

      if (importError) {
        await supabase.storage
          .from("estimate-source-files")
          .remove([storagePath]);
        throw importError;
      }

      const result = data as {
        estimate_id?: string;
        imported_lines?: number;
      } | null;

      if (!result?.estimate_id) {
        throw new Error("A importação terminou sem devolver o orçamento criado.");
      }

      setNotice(
        `LPU importada: ${result.imported_lines ?? sourceLines.length} linhas preservadas.`,
      );

      await onImported(result.estimate_id);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Não foi possível importar a LPU.",
      );
    } finally {
      setImporting(false);
    }
  }

  return (
    <section className="tender-import card">
      <div className="tender-import-head">
        <div>
          <p className="eyebrow">Importação Excel</p>
          <h3>Importar LPU do dono de obra</h3>
          <p className="muted">
            O Excel original é arquivado sem alterações. Código, descrição,
            unidade e quantidade são copiados para o orçamento; os preços ficam
            separados.
          </p>
        </div>
        <button className="button button-secondary" type="button" onClick={onCancel}>
          Fechar
        </button>
      </div>

      <div className="tender-import-file">
        <label className="grow">
          <span>Ficheiro Excel</span>
          <input type="file" accept=".xlsx,.xlsm" onChange={chooseFile} />
        </label>
        {parsing ? <span className="muted">A analisar o Excel…</span> : null}
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      {workbook && selectedSheet ? (
        <>
          <div className="tender-import-meta">
            <label>
              <span>Referência L37</span>
              <input
                placeholder="Ex.: O_060.26"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
              />
            </label>
            <label className="grow">
              <span>Nome da proposta / obra</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              <span>Cliente</span>
              <input
                value={client}
                onChange={(event) => setClient(event.target.value)}
              />
            </label>
            <label className="coef-field">
              <span>Coef. geral</span>
              <input
                inputMode="decimal"
                value={coefficient}
                onChange={(event) => setCoefficient(event.target.value)}
              />
            </label>
          </div>

          <div className="tender-import-mapping">
            <label>
              <span>Folha</span>
              <select
                value={sheetIndex}
                onChange={(event) => changeSheet(Number(event.target.value))}
              >
                {workbook.sheets.map((sheet, index) => (
                  <option value={index} key={sheet.name}>
                    {sheet.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Linha de cabeçalho</span>
              <input
                type="number"
                min={1}
                max={Math.max(1, selectedSheet.rows.length)}
                value={headerRow + 1}
                onChange={(event) =>
                  changeHeaderRow(Math.max(0, Number(event.target.value) - 1))
                }
              />
            </label>

            <label>
              <span>Artigo / código</span>
              <select
                value={mapping.code ?? ""}
                onChange={(event) =>
                  setMapping((current) => ({
                    ...current,
                    code: asColumn(event.target.value),
                  }))
                }
              >
                <option value="">— não usar —</option>
                {headers.map((header) => (
                  <option value={header.index} key={`code-${header.index}`}>
                    {columnLabel(header.index)} — {header.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Descrição *</span>
              <select
                value={mapping.description ?? ""}
                onChange={(event) =>
                  setMapping((current) => ({
                    ...current,
                    description: asColumn(event.target.value),
                  }))
                }
              >
                <option value="">— selecionar —</option>
                {headers.map((header) => (
                  <option
                    value={header.index}
                    key={`description-${header.index}`}
                  >
                    {columnLabel(header.index)} — {header.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Unidade</span>
              <select
                value={mapping.unit ?? ""}
                onChange={(event) =>
                  setMapping((current) => ({
                    ...current,
                    unit: asColumn(event.target.value),
                  }))
                }
              >
                <option value="">— não usar —</option>
                {headers.map((header) => (
                  <option value={header.index} key={`unit-${header.index}`}>
                    {columnLabel(header.index)} — {header.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Quantidade</span>
              <select
                value={mapping.quantity ?? ""}
                onChange={(event) =>
                  setMapping((current) => ({
                    ...current,
                    quantity: asColumn(event.target.value),
                  }))
                }
              >
                <option value="">— não usar —</option>
                {headers.map((header) => (
                  <option
                    value={header.index}
                    key={`quantity-${header.index}`}
                  >
                    {columnLabel(header.index)} — {header.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tender-import-count">
            <strong>{sourceLines.length.toLocaleString("pt-PT")}</strong>
            <span>linhas serão importadas para preço</span>
          </div>

          <div className="tender-preview">
            <div className="tender-preview-row tender-preview-head">
              <span>Art.</span>
              <span>Descrição original</span>
              <span>Un.</span>
              <span>Qtd.</span>
            </div>
            {preview.map((line) => (
              <div className="tender-preview-row" key={line.row_number}>
                <span className="mono">{line.original_code || "—"}</span>
                <span>{line.original_description}</span>
                <span>{line.original_unit || "—"}</span>
                <span>
                  {line.quantity === null
                    ? "—"
                    : Number(line.quantity).toLocaleString("pt-PT", {
                        maximumFractionDigits: 4,
                      })}
                </span>
              </div>
            ))}
          </div>

          <div className="tender-import-actions">
            <p className="muted">
              O ficheiro original fica privado e é usado como fonte de auditoria.
              Esta importação não escreve preços no Excel.
            </p>
            <button
              className="button button-primary"
              type="button"
              disabled={importing || sourceLines.length === 0}
              onClick={() => void importWorkbook()}
            >
              {importing ? "A importar…" : "Criar orçamento a partir desta LPU"}
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
